import React from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export default function UserMenu({ openDrawer }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const theme = useTheme();

  const { logout } = useAuth0();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const handleLogout = () => {
    handleCloseMenu();
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const handleChangePassword = () => {
    handleCloseMenu();
    // Redirect to Auth0's change password page
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const returnTo = encodeURIComponent(window.location.origin);
    
    window.location.href = `https://${auth0Domain}/authorize?response_type=code&client_id=${clientId}&redirect_uri=${returnTo}&screen_hint=reset-password`;
  };

  const userContent = (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: openDrawer ? "column" : "row",
        alignItems: "center",
        gap: 1.5,
        minHeight: 80,
        position: "relative",
        cursor: "pointer",
        transition: theme.transitions.create(["background-color", "padding"], {
          duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleMenuClick}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            backgroundColor: theme.palette.primary.main,
            fontSize: "1rem",
            fontWeight: 600,
            transition: "all 0.2s ease-in-out",
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: `0 0 0 2px ${theme.palette.divider}`,
            "&:hover": {
              transform: "scale(1.05)",
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
            },
          }}
        >
          {user?.fullName?.charAt(0) || "U"}
        </Avatar>
        {openDrawer && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(e);
            }}
            sx={{
              position: "absolute",
              bottom: -6,
              right: -6,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 2px 4px ${theme.palette.action.hover}`,
              width: 20,
              height: 20,
              "&:hover": {
                backgroundColor: theme.palette.grey[100],
              },
              "& .MuiSvgIcon-root": {
                fontSize: 14,
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Box>

      {openDrawer && (
        <Box 
          sx={{ textAlign: "center", minWidth: 0, flex: 1 }}
          onClick={handleMenuClick}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user?.fullName || "User"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user?.email || "user@example.com"}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {openDrawer ? (
        userContent
      ) : (
        <Tooltip title={user?.fullName || "User"} placement="right">
          {userContent}
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            ml: 1,
            minWidth: 180,
            maxWidth: 200,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: `0 4px 20px ${theme.palette.grey[200]}`,
            "& .MuiMenuItem-root": {
              px: 1.5,
              py: 1,
              fontSize: "0.875rem",
              borderRadius: 0.5,
              margin: "1px 2px",
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: theme.transitions.create(["background-color", "color"], {
                duration: theme.transitions.duration.shorter,
              }),
              "&:hover": {
                backgroundColor: theme.palette.primary.light + "15",
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
            "& .MuiListItemIcon-root": {
              minWidth: 'unset',
              color: theme.palette.grey[700],
              "& .MuiSvgIcon-root": {
                color: 'inherit',
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "center" }}
        anchorOrigin={{ horizontal: "right", vertical: "center" }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
              mt: 0,
              zIndex: 1300,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: -6,
                width: 12,
                height: 12,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => { handleCloseMenu(); navigate('/my-account'); }}>
          <ListItemIcon>
            <AccountCircleIcon sx={{ fontSize: 20, color: theme.palette.grey[700] }} />
          </ListItemIcon>
          <ListItemText 
            primary="My Account" 
            primaryTypographyProps={{
              fontSize: "0.875rem",
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => { handleCloseMenu(); navigate('/settings'); }}>
          <ListItemIcon>
            <SettingsIcon sx={{ fontSize: 20, color: theme.palette.grey[700] }} />
          </ListItemIcon>
          <ListItemText 
            primary="Settings" 
            primaryTypographyProps={{
              fontSize: "0.875rem",
            }}
          />
        </MenuItem>
        <MenuItem onClick={handleChangePassword}>
          <ListItemIcon>
            <LockResetIcon sx={{ fontSize: 20, color: theme.palette.grey[700] }} />
          </ListItemIcon>
          <ListItemText 
            primary="Change Password" 
            primaryTypographyProps={{
              fontSize: "0.875rem",
            }}
          />
        </MenuItem>
        <Divider sx={{ my: 0.5, mx: 1 }} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon sx={{ fontSize: 20, color: theme.palette.grey[700] }} />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{
              fontSize: "0.875rem",
            }}
          />
        </MenuItem>
      </Menu>

      {openDrawer && (
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
            }}
          >
            v1.0.0 • © 2024 Disruptum
          </Typography>
        </Box>
      )}
    </>
  );
}
